import { createContext, useContext, useEffect } from 'react';

/**
 * Pagination state a section reports up to the profile shell so the shell can
 * render ONE pinned pager as a footer of the content column (matching the
 * prototype) instead of each section rendering its own inline bar.
 */
export interface SectionPagerState {
  count: number;
  page: number;
  pageSize: number;
  onPage: (page: number) => void;
  onPageSize: (pageSize: number) => void;
}

type SetPager = (state: SectionPagerState | null) => void;

const SectionPagerContext = createContext<SetPager>(() => undefined);

export const SectionPagerProvider = SectionPagerContext.Provider;

/**
 * Called by a section (or Company inner tab) to register its server-paginated
 * list with the shell footer. Only one section is mounted at a time, so the
 * footer always reflects the active section; it clears on unmount. Pass `null`
 * for a section that has no shared pager (e.g. Applications, which renders its
 * own per-group bars).
 *
 * `setPage`/`setPageSize` must be the stable state setters; the hook derives the
 * page-reset-on-size-change behaviour so the pager values stay identity-stable.
 */
export function useSectionPager(
  opts: { count: number; page: number; pageSize: number; setPage: (n: number) => void; setPageSize: (n: number) => void } | null,
): void {
  const setPager = useContext(SectionPagerContext);
  const enabled = !!opts;
  const count = opts?.count ?? 0;
  const page = opts?.page ?? 1;
  const pageSize = opts?.pageSize ?? 10;
  const setPage = opts?.setPage;
  const setPageSize = opts?.setPageSize;

  useEffect(() => {
    if (!enabled || !setPage || !setPageSize) {
      setPager(null);
      return undefined;
    }
    setPager({
      count,
      page,
      pageSize,
      onPage: setPage,
      onPageSize: (s: number) => { setPageSize(s); setPage(1); },
    });
    return () => setPager(null);
  }, [enabled, count, page, pageSize, setPage, setPageSize, setPager]);
}
